// src/components/AccessibilitySettings.tsx
// Accessibility settings component for the Profile/Settings page

import React, { useState } from 'react';
import {
  IonList,
  IonListHeader,
  IonLabel,
  IonItem,
  IonToggle,
  IonSelect,
  IonSelectOption,
  IonText,
  IonIcon,
} from '@ionic/react';
import { eyeOutline, colorPaletteOutline, accessibilityOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { ColorblindTheme } from '../services/accessibilityService';

export const AccessibilitySettings: React.FC = () => {
  const { preferences, updatePreference } = useAccessibility();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = (key: keyof typeof preferences) => {
    if (typeof preferences[key] === 'boolean') {
      updatePreference(key, !preferences[key]);
    }
  };

  const handleColorblindThemeChange = (theme: ColorblindTheme) => {
    updatePreference('colorblindTheme', theme as any);
  };

  return (
    <IonList>
      <IonItem
        button
        onClick={() => setIsExpanded(!isExpanded)}
        detail={false}
      >
        <IonIcon icon={accessibilityOutline} slot="start" />
        <IonLabel>
          <h2>Accessibility Settings</h2>
          <p>Colorblind themes, motion, focus, and more</p>
        </IonLabel>
        <IonIcon
          icon={isExpanded ? chevronUpOutline : chevronDownOutline}
          slot="end"
        />
      </IonItem>

      {isExpanded && (
        <>
          {/* Colorblind Theme Selector */}
          <IonItem>
          <IonIcon icon={colorPaletteOutline} slot="start" />
          <IonLabel>
            <h2>Colorblind Theme</h2>
            <p>Choose a color palette optimized for color vision</p>
          </IonLabel>
          <IonSelect
            value={preferences.colorblindTheme}
            onIonChange={(e) => handleColorblindThemeChange(e.detail.value)}
            interface="action-sheet"
            aria-label="Select colorblind theme"
          >
            <IonSelectOption value="none">None</IonSelectOption>
            <IonSelectOption value="protanopia">Protanopia (Red-Blind)</IonSelectOption>
            <IonSelectOption value="deuteranopia">Deuteranopia (Green-Blind)</IonSelectOption>
            <IonSelectOption value="tritanopia">Tritanopia (Blue-Blind)</IonSelectOption>
          </IonSelect>
        </IonItem>

        {/* Reduced Motion */}
        <IonItem>
          <IonLabel>
            <h2>Reduced Motion</h2>
            <p>Minimize animations and transitions</p>
          </IonLabel>
          <IonToggle
            checked={preferences.reducedMotion}
            onIonChange={() => handleToggle('reducedMotion')}
            aria-label="Toggle reduced motion"
          />
        </IonItem>

        {/* Enhanced Focus */}
        <IonItem>
          <IonLabel>
            <h2>Enhanced Focus Indicators</h2>
            <p>Stronger visual focus indicators for keyboard navigation</p>
          </IonLabel>
          <IonToggle
            checked={preferences.enhancedFocus}
            onIonChange={() => handleToggle('enhancedFocus')}
            aria-label="Toggle enhanced focus indicators"
          />
        </IonItem>

        {/* High Contrast */}
        <IonItem>
          <IonLabel>
            <h2>High Contrast</h2>
            <p>Increase text and UI element contrast</p>
          </IonLabel>
          <IonToggle
            checked={preferences.highContrast}
            onIonChange={() => handleToggle('highContrast')}
            aria-label="Toggle high contrast"
          />
        </IonItem>

        {/* Verbose Screen Reader */}
        <IonItem>
          <IonLabel>
            <h2>Verbose Screen Reader</h2>
            <p>More detailed announcements for screen readers</p>
          </IonLabel>
          <IonToggle
            checked={preferences.verboseScreenReader}
            onIonChange={() => handleToggle('verboseScreenReader')}
            aria-label="Toggle verbose screen reader"
          />
        </IonItem>

        {/* Larger Touch Targets */}
        <IonItem>
          <IonLabel>
            <h2>Larger Touch Targets</h2>
            <p>Increase size of buttons and interactive elements</p>
          </IonLabel>
          <IonToggle
            checked={preferences.largerTouchTargets}
            onIonChange={() => handleToggle('largerTouchTargets')}
            aria-label="Toggle larger touch targets"
          />
        </IonItem>

        {/* Simplified Navigation */}
        <IonItem>
          <IonLabel>
            <h2>Simplified Navigation</h2>
            <p>Streamlined interface with skip links</p>
          </IonLabel>
          <IonToggle
            checked={preferences.simplifiedNavigation}
            onIonChange={() => handleToggle('simplifiedNavigation')}
            aria-label="Toggle simplified navigation"
          />
        </IonItem>

        <IonItem lines="none">
          <IonText color="medium" style={{ fontSize: '0.875rem', padding: '8px 0' }}>
            <p>
              <IonIcon icon={eyeOutline} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              All accessibility features are free for everyone
            </p>
          </IonText>
        </IonItem>
        </>
      )}
    </IonList>
  );
};
